import { useQueryClient } from '@tanstack/react-query';

import type {
  Bdkv2BookDescriptor,
  Bdkv2Value,
  V2BookConnectionDescriptor,
  V2BookConnectionResponse,
} from '@/generated/hooks/bdk/models';
import {
  getRetrieveAllWorkspaceBookConnectionsQueryKey,
  getRetrieveWorkspaceBookConnectionsQueryKey,
  useAddWorkspaceBookConnectionWithRemoteSecret,
  useUpdateWorkspaceBookConnectionWithRemoteSecret,
} from '@/generated/hooks/bdk/v2/services/library';

/** Where a connection is written, when not the hook's own workspace. */
export interface IConnectionWorkspace {
  organizationId: string;
  workspaceId: string;
}

/** Arguments for creating a connection backed by a managed credential. */
export interface ICreateRemoteSecretConnectionArgs {
  integration: Bdkv2BookDescriptor;
  connectionName: string;
  authMethodId: string;
  /** The managed credential's id, stored as the connection's `credentials_id`. */
  credentialId: string;
  /** Environment labels, e.g. `['draft', 'published']`. */
  labels: string[];
  configMap?: Record<string, Bdkv2Value>;
  remoteConnectionId?: string;
  /** Defaults to the hook's workspace. */
  workspace?: IConnectionWorkspace;
}

/** Arguments for re-pointing a connection created in this wizard. */
export interface IUpdateRemoteSecretConnectionArgs {
  integration: Bdkv2BookDescriptor;
  connectionId: string;
  connectionName: string;
  authMethodId: string;
  credentialId: string;
  labels: string[];
  configMap?: Record<string, Bdkv2Value>;
}

/** Arguments for saving an existing connection from the edit dialog. */
export interface ISaveRemoteSecretConnectionArgs {
  integration: Bdkv2BookDescriptor;
  /** The connection as edited (name and labels are kept from it). */
  connection: V2BookConnectionDescriptor;
  credentialId: string;
  configMap?: Record<string, Bdkv2Value>;
  /** Defaults to the hook's workspace. */
  workspace?: IConnectionWorkspace;
}

const toLabels = (labels: string[]) =>
  labels.map(label => ({ key: `${label}_connection`, value: 'true' }));

/**
 * Connection writes for integrations that support remote secrets. These go to
 * the dedicated `with-remote-secret` endpoints, which take a managed
 * credential's id instead of inline authentication values.
 *
 * @param organizationId - Current organization id.
 * @param workspaceId - Current workspace id.
 * @returns Create and update actions.
 */
export function useRemoteSecretConnectionActions(
  organizationId: string,
  workspaceId: string,
) {
  const queryClient = useQueryClient();
  const { mutateAsync: addConnection } =
    useAddWorkspaceBookConnectionWithRemoteSecret();
  const { mutateAsync: updateConnection } =
    useUpdateWorkspaceBookConnectionWithRemoteSecret();

  const invalidateConnections = (
    integration: Bdkv2BookDescriptor,
    target: IConnectionWorkspace = { organizationId, workspaceId },
  ) => {
    void queryClient.invalidateQueries({
      queryKey: getRetrieveAllWorkspaceBookConnectionsQueryKey(
        target.organizationId,
        target.workspaceId,
      ),
    });
    void queryClient.invalidateQueries({
      queryKey: getRetrieveWorkspaceBookConnectionsQueryKey(
        target.organizationId,
        target.workspaceId,
        integration.name,
        integration.version,
      ),
    });
  };

  const createConnectionWithRemoteSecret = async ({
    integration,
    connectionName,
    authMethodId,
    credentialId,
    labels,
    configMap,
    remoteConnectionId,
    workspace = { organizationId, workspaceId },
  }: ICreateRemoteSecretConnectionArgs): Promise<V2BookConnectionResponse> => {
    const response = await addConnection({
      ...workspace,
      bookName: integration.name || '',
      bookVersion: integration.version || '',
      data: {
        connection_name: connectionName,
        authentication_id: authMethodId,
        credentials_id: credentialId,
        labels: toLabels(labels),
        ...(configMap && Object.keys(configMap).length > 0
          ? { config_map: configMap }
          : {}),
        ...(remoteConnectionId
          ? { associated_remote_connection_id: remoteConnectionId }
          : {}),
      },
    });
    invalidateConnections(integration, workspace);
    return response.data;
  };

  const updateConnectionWithRemoteSecret = async ({
    integration,
    connectionId,
    connectionName,
    authMethodId,
    credentialId,
    labels,
    configMap,
  }: IUpdateRemoteSecretConnectionArgs): Promise<V2BookConnectionResponse> => {
    const response = await updateConnection({
      organizationId,
      workspaceId,
      bookName: integration.name || '',
      bookVersion: integration.version || '',
      connectionId,
      data: {
        connection_name: connectionName,
        authentication_id: authMethodId,
        credentials_id: credentialId,
        labels: toLabels(labels),
        ...(configMap ? { config_map: configMap } : {}),
      },
    });
    invalidateConnections(integration);
    return response.data;
  };

  /** Saves an existing connection, keeping its auth method and labels. */
  const saveConnectionWithRemoteSecret = async ({
    integration,
    connection,
    credentialId,
    configMap,
    workspace = { organizationId, workspaceId },
  }: ISaveRemoteSecretConnectionArgs): Promise<V2BookConnectionResponse> => {
    const response = await updateConnection({
      ...workspace,
      bookName: integration.name || '',
      bookVersion: integration.version || '',
      connectionId: connection.connection_id || '',
      data: {
        connection_name: connection.connection_name,
        authentication_id: connection.authentication?.authentication_id,
        credentials_id: credentialId,
        labels: connection.labels,
        config_map: configMap ?? connection.config_map,
      },
    });
    invalidateConnections(integration, workspace);
    return response.data;
  };

  return {
    createConnectionWithRemoteSecret,
    updateConnectionWithRemoteSecret,
    saveConnectionWithRemoteSecret,
  };
}
